'use client';

/**
 * The shared page's map, held over the planner's map while it wakes up after
 * "Take this trip": the same picture (the browser already has it), the same
 * day-coloured road and the same pins. It fades away once the live map has
 * drawn the route, so the hand-off never shows a bare outline of Australia.
 * z-[6]: above the map's own first-paint outline (MapFirstPaint, z-[5]),
 * below its chip row and buttons (z-10).
 */
import React, { useEffect, useRef, useState } from 'react';
import type { TripStop } from '../../types/trip';
import StaticRouteMap from '../../trip/shared/[token]/StaticRouteMap';
import { staticView, viewFrom } from '../../trip/shared/[token]/sharedTrip';
import { groupPins } from '../../trip/shared/[token]/SharedTripMap';
import type { TakenRoute } from './takeTrip';

/** How long the live map gets, after drawing the route, to paint its tiles. */
export const COVER_HOLD_MS = 500;

export default function TakenRouteCover({ stops, route, ready }: { stops: TripStop[]; route: TakenRoute; ready: boolean }) {
  const [gone, setGone] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // Leave once the live map has drawn the route AND its tiles are on screen
  // (the map marks itself data-map-settled="1"), never while it is still the
  // drawn outline of Australia.
  useEffect(() => {
    if (!ready) return;
    let t1 = 0;
    let t2 = 0;
    let poll = 0;
    // A beat first: drawing the route re-frames the camera, which un-settles
    // the map; a "settled" from before that would be the outline's.
    const start = window.setTimeout(() => {
      poll = window.setInterval(() => {
        const host = ref.current?.parentElement;
        if (!host?.querySelector('[data-map-settled="1"]')) return;
        window.clearInterval(poll);
        t1 = window.setTimeout(() => setLeaving(true), COVER_HOLD_MS);
        t2 = window.setTimeout(() => setGone(true), COVER_HOLD_MS + 520);
      }, 200);
    }, 400);
    return () => {
      window.clearTimeout(start);
      window.clearInterval(poll);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [ready]);
  // Never hold it forever: a map that can't load still has its own line.
  useEffect(() => {
    const t = window.setTimeout(() => setLeaving(true), 20000);
    const t2 = window.setTimeout(() => setGone(true), 20600);
    return () => {
      window.clearTimeout(t);
      window.clearTimeout(t2);
    };
  }, []);
  if (gone) return null;
  const pts = route.lines.flatMap((l) => l.path);
  const view = (route.view && viewFrom(route.view)) || staticView(pts, 640, 540, { pad: 48 });
  if (!view) return null;
  return (
    <div
      ref={ref}
      aria-hidden="true"
      data-testid="taken-route-cover"
      className={`pointer-events-none absolute inset-0 z-[6] transition-opacity duration-500 ease-tp-out motion-reduce:transition-none ${leaving ? 'opacity-0' : 'opacity-100'}`}
    >
      <StaticRouteMap
        view={view}
        lines={route.lines.map((l, i) => ({ key: String(i), color: l.color, path: l.path, routed: true }))}
        pins={groupPins(stops).map((g) => ({ key: g.key, position: g.position, letter: g.letters[0], role: g.role, loop: g.loop }))}
      />
    </div>
  );
}
